import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApi } from '@/hooks/useApi';
import AddCompanyModal from './components/AddCompanyModal';
import EditCompanyModal from './components/EditCompanyModal';
import DeleteConfirmModal from '@/components/base/DeleteConfirmModal';

import { getFileUrl } from '@/utils/fileUrl';

interface Company {
  id: number;
  name: string;
  slug: string;
  contactEmail: string;
  contactPhone: string;
  isActive: boolean;
  isDeleted: number;
  deletedAt: string | null;
  createdAt: string;
  totalUsers: number;
  totalCustomers: number;
  logoUrl: string | null;
}

export default function Companies() {
  const { put, del } = useApi();
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editCompany, setEditCompany] = useState<Company | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string; mode: 'soft' | 'hard' } | null>(null);

  const { data: companies, loading, refetch } = useApiQuery<Company[]>({
    endpoint: `/companies${showDeleted ? '?showDeleted=true' : ''}`,
  });

  const filtered = companies?.filter((c) =>
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.contactEmail || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget.mode !== 'soft') return;
    await put(`/companies/${deleteTarget.id}/soft-delete`, {});
    setDeleteTarget(null);
    refetch();
  }, [deleteTarget, put, refetch]);

  const handleHardDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget.mode !== 'hard') return;
    await del(`/companies/${deleteTarget.id}`);
    setDeleteTarget(null);
    refetch();
  }, [deleteTarget, del, refetch]);

  const handleRestore = useCallback(async (id: number) => {
    await put(`/companies/${id}/restore`, {});
    refetch();
  }, [put, refetch]);

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Companies</h1>
          <p className="text-stone-500 text-sm mt-1">Manage all tenant organizations on the platform.</p>
        </div>
        <button
          onClick={() => setAddModalOpen(true)}
          className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line" />
          Add Company
        </button>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-xl border border-stone-100 p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="relative max-w-md flex-1 w-full">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm w-4 h-4 flex items-center justify-center" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search companies..."
            className="w-full border border-stone-200 rounded-lg pl-10 pr-4 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
          />
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          />
          <div className="w-9 h-5 bg-stone-300 peer-checked:bg-brand-500 rounded-full transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
          <span className="text-stone-600 text-sm">Show deleted</span>
        </label>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin w-8 h-8 flex items-center justify-center mx-auto" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Company</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Users</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Customers</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered?.map((company) => (
                  <tr key={company.id} className={`hover:bg-stone-50 transition-colors ${company.isDeleted ? 'bg-stone-50/60' : ''}`}>
                    <td className="px-4 py-3">
                      <Link to={`/companies/${company.id}`} className="flex items-center gap-3 group cursor-pointer">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 overflow-hidden ${company.isDeleted ? 'bg-stone-200' : 'bg-brand-100'}`}>
                          {company.logoUrl ? (
                            <img src={getFileUrl(company.logoUrl)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className={`font-bold text-xs ${company.isDeleted ? 'text-stone-400' : 'text-brand-700'}`}>
                              {company.name.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className={`text-sm font-medium group-hover:text-brand-600 transition-colors ${company.isDeleted ? 'text-stone-400 line-through' : 'text-stone-900'}`}>
                            {company.name}
                          </p>
                          <p className="text-stone-400 text-xs">{company.slug}</p>
                          {company.isDeleted && company.deletedAt && (
                            <p className="text-red-400 text-[10px]">Deleted {new Date(company.deletedAt).toLocaleDateString()}</p>
                          )}
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <p className={`text-sm ${company.isDeleted ? 'text-stone-400' : 'text-stone-900'}`}>{company.contactEmail}</p>
                      <p className="text-stone-400 text-xs">{company.contactPhone}</p>
                    </td>
                    <td className="px-4 py-3 text-sm">{company.totalUsers}</td>
                    <td className="px-4 py-3 text-sm">{company.totalCustomers}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        company.isDeleted ? 'bg-red-50 text-red-700' :
                        company.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {company.isDeleted ? 'Deleted' : company.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-sm">{new Date(company.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {!company.isDeleted && (
                          <>
                            <button
                              onClick={() => setEditCompany(company)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors cursor-pointer"
                              title="Edit"
                            >
                              <i className="ri-pencil-line text-sm w-4 h-4 flex items-center justify-center" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget({ id: company.id, name: company.name, mode: 'soft' })}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-red-600 transition-colors cursor-pointer"
                              title="Deactivate"
                            >
                              <i className="ri-delete-bin-line text-sm w-4 h-4 flex items-center justify-center" />
                            </button>
                          </>
                        )}
                        {company.isDeleted && (
                          <>
                            <button
                              onClick={() => handleRestore(company.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-stone-400 hover:text-emerald-600 transition-colors cursor-pointer"
                              title="Reactivate"
                            >
                              <i className="ri-arrow-go-back-line text-sm w-4 h-4 flex items-center justify-center" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget({ id: company.id, name: company.name, mode: 'hard' })}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors cursor-pointer"
                              title="Permanently delete"
                            >
                              <i className="ri-fire-line text-sm w-4 h-4 flex items-center justify-center" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered?.length === 0 && (
              <div className="p-8 text-center">
                <i className="ri-building-4-line text-stone-300 text-3xl mb-2 w-12 h-12 flex items-center justify-center mx-auto" />
                <p className="text-stone-400 text-sm">No companies found.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <AddCompanyModal open={addModalOpen} onClose={() => setAddModalOpen(false)} onSuccess={refetch} />
      <EditCompanyModal open={!!editCompany} onClose={() => setEditCompany(null)} onSuccess={refetch} company={editCompany} />
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteTarget?.mode === 'hard' ? handleHardDelete : handleSoftDelete}
        title={deleteTarget?.mode === 'hard' ? 'Permanent Delete' : 'Deactivate Company'}
        entityName={deleteTarget?.name}
        description={
          deleteTarget?.mode === 'hard'
            ? 'This will permanently erase the company profile, logo file, and all linked users, customers, KYC requests and documents. This action cannot be undone.'
            : 'The company profile will be disabled and hidden from the list. You can reactivate it later.'
        }
      />
    </div>
  );
}